<template>
  <div class="creation-demand-step-booking-express">
    <section class="q-mt-md q-mb-xl q-pb-md q-px-md q-pt-xs bg-white rounded-borders bg-white shadow-soft">
      <h2 class="step-title">
        {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.DEMANDS.CREATION.@NUMBER_TITLE")) }}
      </h2>
      <div class="creation-number-selection">
        <Numerator v-model="numberOfExtra" :min="min" :max="max" />
      </div>
    </section>
    <!-- Booking express section -->
    <section v-if="isBookingExpressStepAvailable" class="creation-booking-express-section">
      <ExtraTipbox v-if="!favoritesList.length" class="offer-etab-contact">
        {{ $t("ETAB.PAGES.DEMANDS.CREATION.@NO_FAVORITES") }}
      </ExtraTipbox>
      <ExtraTipbox v-if="favoritesList.length > 0 && !availableFavoritesList.length" class="offer-etab-contact">
        {{ $t("ETAB.PAGES.DEMANDS.CREATION.@NO_FAVORITE_EXTRAS_AVAILABLE") }}
      </ExtraTipbox>
      <ExtraTipbox v-if="availableFavoritesList.length > 0 && numberOfExtra > 1" class="offer-etab-contact">
        {{ $t("ETAB.PAGES.DEMANDS.CREATION.@TOO_MANY_EXTRAS") }}
      </ExtraTipbox>
      <div
        v-if="showFavorites && selectedFavorite"
        class="q-mt-lg q-pt-xs bg-white rounded-borders bg-white shadow-soft text-center relative-position"
      >
        <ExtraTag class="optional-tag" background="green" :label="$t('ETAB.PAGES.DEMANDS.CREATION.@FAVORITE_SELECTED_TAG')" />
        <QItem class="q-my-md bg-white rounded-borders bg-white be-candidates-list-item">
          <QItemSection avatar>
            <Avatar :picture="selectedFavorite.getPicture()" size="50px" />
          </QItemSection>
          <QItemSection class="text-left">
            <QItemLabel>
              <p class="text-weight-bold q-ma-none">
                {{ selectedFavorite.getFullName() }}
              </p>
            </QItemLabel>
            <QItemLabel>
              <QRating :model-value="selectedFavorite.getRating()" size="xs" color="orange" readonly disable />
            </QItemLabel>
          </QItemSection>
          <QItemSection side>
            <QItemLabel lines="1">
              <span class="recruitment-candidate-card-jobs">
                {{ $tc("ETAB.PAGES.DEMANDS.CREATION.@NUMBER_MISSION", selectedFavorite.getMissionNumber()) }}
              </span>
            </QItemLabel>
            <QItemLabel>
              <QBtn dense class="q-px-sm" no-caps color="negative" @click="setSelectedFavorite(undefined)">
                <QIcon name="fal fa-xmark" size="xs" class="q-mr-xs" />
                {{ $t("CORE.BUTTONS.@REMOVE") }}
              </QBtn>
            </QItemLabel>
          </QItemSection>
        </QItem>
      </div>
      <div
        v-if="showFavorites && !selectedFavorite"
        class="q-mt-lg q-pt-lg bg-white rounded-borders bg-white shadow-soft text-center relative-position"
      >
        <ExtraTag class="optional-tag" background="secondary" :label="$t('ETAB.BUTTONS.@OPTIONAL')" />
        <p class="text-primary q-mx-md">
          {{ $filters.whitespacedPunctuation($t("ETAB.PAGES.DEMANDS.CREATION.@BOOKING_EXPRESS")) }}
        </p>

        <div class="creation-booking-express">
          <div v-if="availableFavoritesList.length" class="creation-booking-express-search q-mx-md">
            <QInput
              v-model="filterFavorites"
              class="creation-booking-express-search-bar full-width"
              filled
              :label="$t('ETAB.PAGES.DEMANDS.CREATION.@SEARCH_LABEL')"
              clearable
            >
              <template #prepend>
                <QIcon name="search" />
              </template>
            </QInput>
          </div>
          <QItem v-if="filterFavorites && !favoriteExtras.length" class="q-my-md bg-white rounded-borders border">
            <QItemSection>
              <QItemLabel>
                <p class="text-weight-bold q-ma-none">
                  {{ $t("ETAB.PAGES.DEMANDS.CREATION.@NO_RESULT") }}
                </p>
              </QItemLabel>
            </QItemSection>
          </QItem>
          <div v-for="(favorite, index) in favoriteExtras" :key="index">
            <QItem class="q-my-md bg-white rounded-borders be-candidates-list-item">
              <span v-if="!favorite.isAvailable()" class="be-candidates-list-item-overlay">
                {{ $t("ETAB.PAGES.DEMANDS.CREATION.@UNAVAILABLE_CANDIDATE") }}
              </span>
              <QItemSection avatar>
                <Avatar :picture="favorite.getPicture()" :size="$q.screen.lt.md ? '36px' : '50px'" />
              </QItemSection>
              <QItemSection class="text-left">
                <QItemLabel>
                  <p class="text-weight-bold q-ma-none">
                    {{ favorite.getFullName() }}
                  </p>
                </QItemLabel>
                <QItemLabel>
                  <QRating :model-value="favorite.getRating()" :size="$q.screen.lt.md ? '1rem' : 'xs'" color="orange" readonly disable />
                </QItemLabel>
              </QItemSection>
              <QItemSection side>
                <QItemLabel lines="1">
                  <span class="recruitment-candidate-card-jobs">
                    {{ $tc("ETAB.PAGES.DEMANDS.CREATION.@NUMBER_MISSION", favorite.getMissionNumber()) }}
                  </span>
                </QItemLabel>
                <QItemLabel>
                  <QBtn
                    v-if="favorite.isAvailable()"
                    dense
                    class="q-px-sm"
                    :disable="!!selectedFavorite"
                    no-caps
                    outline
                    color="secondary"
                    :label="$t('ETAB.PAGES.DEMANDS.CREATION.@SELECT_CANDIDATE')"
                    @click="setSelectedFavorite(favorite)"
                  />
                </QItemLabel>
              </QItemSection>
            </QItem>
          </div>
        </div>
      </div>
    </section>
    <transition appear enter-active-class="animated fadeIn slower delay-1s" leave-active-class="animated fadeOut">
      <section class="q-footer q-layout__section--marginal fixed-bottom">
        <div>
          <QBtn
            color="secondary"
            class="full-width btn-navigation"
            :label="$t('ETAB.BUTTONS.@VALIDATE')"
            :disabled="!isNumberSet"
            no-caps
            :loading="loading"
            @click.once="save"
          />
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
  import { BookingExpressCandidateModel, CreationStepExtraNumberModel, ICreationStepCandidate, ICreationStepExtraNumber } from "@core/models";
  import Numerator from "@core/components/forms/Numerator.component.vue";

  // eslint-disable-next-line no-undef
  import placeholderImg from "@core/assets/img/user_photo_placeholder.svg";

  export default {
    components: {
      Numerator,
    },
    props: {
      stepAssets: {
        type: Object,
        default: () => ({}),
      },
      stepData: {
        type: Object,
        default: () => ({}),
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["saveStep"],
    data() {
      return {
        show: true,
        numberOfExtra: 1,
        min: 1,
        max: null,
        filterFavorites: "",
        selectedFavorite: null,
      };
    },
    computed: {
      isBookingExpressStepAvailable() {
        return !!this.stepAssets.booking_express_available;
      },
      favoritesList() {
        return (this.stepAssets.candidates || []).map((favorite) => new BookingExpressCandidateModel(favorite));
      },
      showFavorites() {
        return this.isBookingExpressStepAvailable && this.availableFavoritesList.length > 0 && this.numberOfExtra < 2;
      },
      isNumberSet() {
        return this.numberOfExtra !== 0;
      },
      stepDataModel() {
        return new CreationStepExtraNumberModel(this.stepData);
      },
      availableFavoritesList() {
        return this.favoritesList.filter((favorite) => favorite.isAvailable());
      },
      favoriteExtras() {
        const { filterFavorites, favoritesList, selectedFavorite } = this;
        return favoritesList.filter((extra) => {
          const matchSearch =
            extra
              .getFirstName()
              .toLowerCase()
              .includes(filterFavorites?.toLowerCase() || "") ||
            extra
              .getLastName()
              .toLowerCase()
              .includes(filterFavorites?.toLowerCase() || "");
          const extraSelected = selectedFavorite ? extra.getId() === selectedFavorite.getId() : false;
          return matchSearch && !extraSelected;
        });
      },
    },
    created() {
      this.numberOfExtra = this.stepDataModel.getQuantity();
      if (this.stepData[ICreationStepCandidate.candidate]) {
        this.selectedFavorite = this.favoritesList.find((candidate) => candidate.getId() === this.stepData[ICreationStepCandidate.candidate]);
      }
    },
    beforeUnmount() {
      this.show = false;
    },
    methods: {
      setSelectedFavorite(value) {
        this.selectedFavorite = value;
      },
      save() {
        const candidate = this.numberOfExtra === 1 ? this.selectedFavorite?.getId() : undefined;
        this.$emit("saveStep", {
          [ICreationStepExtraNumber.quantity]: this.numberOfExtra,
          candidate,
        });
      },
      displayPicture(picture) {
        return picture || placeholderImg;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .creation-demand-step-booking-express {
    .creation-booking-express-section {
      margin-bottom: 80px;
      .optional-tag {
        position: absolute;
        left: 50%;
        top: 0;
        margin-bottom: 16px;
        margin-top: -14px;
        transform: translateX(-50%);
      }
      .creation-booking-express {
        > p {
          margin-bottom: 5px;
        }

        .be-candidates-list-item {
          position: relative;
          &-overlay {
            position: absolute;
            top: 0;
            bottom: 0;
            right: 0;
            left: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            color: white;
            background-color: rgba($primary, 0.65);
            z-index: 1;
            cursor: not-allowed;
          }
          .list-item-btns {
            position: absolute;
            bottom: 0;
            left: 0;
            height: 60px;
            width: 100%;
            display: flex;
            flex-direction: row;
            justify-content: space-around;
            align-items: stretch;
            border-top: 1px solid rgba($primary, 0.1);
          }
        }
      }
    }
  }
</style>
